import {
    getProduct,
    getProductCategories,
    ECAPI_TYPE_CAPSULE,
    ECAPI_TYPE_GIFT_CARD
} from '@kissui/helpers/src/catalog'

/**
 * Api fetcher service
 *
 * @returns {object}
 */
const fetcher = () => {
    const apiRatings = window.napi && window.napi.ratings
    const apiPriceFormat = window.napi && window.napi.priceFormat
    let priceFormatter = null

    const loadPriceFormat = async () => {
        priceFormatter = await apiPriceFormat()
    }

    const getApiProduct = async sku => await getProduct(sku)

    /**
     * Get all products merged with data coming from API
     *
     * @returns {Promise<array>}list of products
     * @param productList
     * @param withRating
     */
    const getAll = (productList, withRating) =>
        Promise.all(
            productList.map(async product => {
                try {
                    const fetchedProduct = await getOne(product, withRating)
                    productList.splice(productList.indexOf(product), 1, fetchedProduct)
                } catch (error) {
                    productList.splice(productList.indexOf(product), 1)
                }
            })
        )

    /**
     * Get product merged with data coming from API
     *
     * @returns {Promise} product
     * @param product
     * @param withRating
     */
    const getOne = async (product, withRating) => {
        try {
            return await fetchOne(product, withRating)
        } catch (error) {
            console.error(`Fetch for product ${product.sku} failed: ${error}`)
            throw error
        }
    }

    /**
     * Fetch and merge product with data coming from API
     *
     * @param {object} product - base product from static data
     * @param apiProduct
     * @param withRating
     * @param list
     * @returns {Promise<object>} Product
     */
    const fetchOne = async (product, withRating) => {
        const { api_override: { name = '', headline = '', description = '' } = {} } = product

        const apiProduct = await getApiProduct(product.sku)
        const productCategories = await getProductCategories(product.sku)

        const overrides = {
            name: name !== '' ? name : apiProduct.name,
            headline: headline !== '' ? headline : apiProduct.headline,
            description: description !== '' ? description : apiProduct.description,
            longSku: apiProduct ? apiProduct.id : null,
            price: apiProduct ? apiProduct.price : null,
            pdp_urls: getValueObject(apiProduct, 'pdpURLs'),
            images: apiProduct.images,
            slides: apiProduct.slides,
            technologies: apiProduct.technologies,
            type: apiProduct.type,
            productCategories
        }

        if (withRating && product.productRatings?.ratingEnabled) {
            overrides.rating = await getRating(product, apiProduct)
        }

        if (apiProduct.type === ECAPI_TYPE_CAPSULE) {
            overrides.bundled = apiProduct.bundled
            overrides.intensity = apiProduct.capsuleProperties?.intensity || 0
            overrides.unit_quantity = getValueNumber(apiProduct, 'unitQuantity', 1)
            overrides.sales_multiple = getValueNumber(apiProduct, 'salesMultiple', 10)
        }

        if (apiProduct.type === ECAPI_TYPE_GIFT_CARD) {
            if (!product.variant.giftcard_price) {
                throw new Error('Gift Card amount price is not defined')
            }

            overrides.price = product.variant.giftcard_price
            overrides.images = { icon: product.variant.default_image }
        }

        return { ...merge(product, apiProduct), ...overrides }
    }

    /**
     * Merge products based on static product existing keys
     * A shallow copy is made to prevent original object mutation
     *
     * @param {object} product
     * @param {object} apiProduct
     * @param {boolean} force Force property override
     * @returns {object} Product
     */
    const merge = (product, apiProduct, force = false) => {
        const mergedProduct = { ...product }

        if (!apiProduct) {
            return mergedProduct
        }

        for (const key of Object.keys(mergedProduct)) {
            const empty = mergedProduct[key] === '' || mergedProduct[key] === '0'
            if (key in apiProduct && (empty || force)) {
                mergedProduct[key] = apiProduct[key]
            }
        }

        return mergedProduct
    }

    /**
     * @param {object} apiProduct
     * @returns {number}
     */
    const getValueNumber = (apiProduct, key, defaultValue) =>
        parseInt((apiProduct && apiProduct[key]) || defaultValue)

    /**
     * @param {object} apiProduct
     * @returns {object}
     */
    const getValueObject = (apiProduct, key) => {
        return apiProduct && Object.keys(apiProduct[key]).length > 0 ? apiProduct[key] : {}
    }

    /**
     * @param {string} sku
     * @returns {Promise<number>} Rating value
     */
    const getRating = async ({ sku }, apiProduct) => {
        if (
            !apiRatings ||
            (apiProduct && apiProduct.productRatings && !apiProduct.productRatings.ratingEnabled)
        ) {
            return toFloat(0, 1)
        }

        try {
            const { ratingAverage = 0 } = await apiRatings().summary(sku)
            return toFloat(ratingAverage !== 0 ? ratingAverage : 0, 1)
        } catch (error) {
            console.error(`Product rating fetch failed on sku ${sku}`, error)
            return toFloat(0, 1)
        }
    }

    /**
     * Get currency from datalayer
     * @returns {string}
     */
    const _getCurrency = () => {
        const { dataLayer = {} } = window[window.config.padl.namespace]
        return dataLayer.app?.app?.currency
    }

    /**
     * @returns {string}
     * @param price
     */
    const formatPrice = price => {
        const { short } = priceFormatter
        return short(_getCurrency(), price)
    }

    /**
     * @param {number} value
     * @param {number} decimal
     * @returns {number}
     */
    const toFloat = (value, decimal = 2) => parseFloat(value).toFixed(decimal)

    return {
        loadPriceFormat,
        getApiProduct,
        getAll,
        getOne,
        fetchOne,
        merge,
        formatPrice
    }
}

export default fetcher()
